import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { ArrowLeft, Check, SkipForward, X } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

import { ProposalCard } from "./ProposalCard";
import { useSwipeGesture } from "./useSwipeGesture";
import { useProposalQueue } from "./useProposalQueue";

/**
 * The proposal review deck. Refs #24's last open box.
 *
 * A sibling of SwipeRunner, reusing its two genuinely scenario-agnostic
 * pieces — the drag gesture (useSwipeGesture) and the card-stack chrome
 * (backdrop card behind, drag transform on the active one) — rather than
 * forking that file. What SwipeRunner is NOT reused for is the queue engine
 * (useSwipeQueue): that hook is contact-shaped in a way a proposal is not —
 * it applies a `ContactPatch` with `dataProvider.update`, optimistically,
 * with undo. A proposal's "answer" is a real, possibly-failing execution on
 * the server, never a patch and never undoable, so it gets its own hook
 * (useProposalQueue) instead of a `ContactPatch`-shaped scenario bent to fit.
 *
 * Gestures: right = Approve, left = Reject (opens the reason prompt — a
 * rejection cannot fire without one, see proposalReview.ts), up/down = Skip.
 * Only two swipe-bound verbs, unlike the contact deck's up-to-four, because a
 * proposal only ever has two ways to answer plus "not now".
 */
export const ProposalDeck = () => {
  const queue = useProposalQueue();
  const {
    current,
    next,
    outcome,
    isWorking,
    isPending,
    error,
    isDemo,
    isExhausted,
    remaining,
    approve,
    reject,
    skip,
    continueAfterOutcome,
  } = queue;

  const [rejecting, setRejecting] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");

  // A new card (or a fresh outcome) always closes a stale reject prompt from
  // the card before it.
  useEffect(() => {
    setRejecting(false);
    setRejectionReason("");
  }, [current?.id]);

  const canAct = Boolean(current) && !isWorking && !outcome;

  const commit = useCallback(
    (direction: "right" | "left" | "up" | "down") => {
      if (!canAct) return;
      if (direction === "right") approve();
      else if (direction === "left") setRejecting(true);
      else skip();
    },
    [canAct, approve, skip],
  );

  const gesture = useSwipeGesture(commit, canAct && !rejecting);

  const submitRejection = useCallback(() => {
    if (!rejectionReason.trim()) return;
    reject(rejectionReason);
    setRejecting(false);
  }, [reject, rejectionReason]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest("input,textarea,[contenteditable=true]")) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      if (outcome) {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          continueAfterOutcome();
        }
        return;
      }
      if (!canAct) return;
      switch (event.key) {
        case "ArrowRight":
          event.preventDefault();
          approve();
          break;
        case "ArrowLeft":
          event.preventDefault();
          setRejecting(true);
          break;
        case "ArrowDown":
        case "s":
          event.preventDefault();
          skip();
          break;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [outcome, canAct, approve, skip, continueAfterOutcome]);

  // Pinned once per mount so a still-open card's age reads consistently
  // rather than ticking during a review session.
  const now = useMemo(() => new Date(), []);

  return (
    <div className="mx-auto flex h-[calc(100dvh-9.5rem)] w-full max-w-md flex-col gap-2 sm:h-auto sm:gap-3">
      <header className="flex items-center gap-2">
        <Button variant="ghost" size="sm" asChild className="-ml-2 shrink-0">
          <Link to="/swipe" aria-label="Back to triage">
            <ArrowLeft className="h-4 w-4" aria-hidden />
            <span className="hidden sm:inline">Triage</span>
          </Link>
        </Button>
        <h1 className="text-lg leading-tight font-semibold sm:text-xl">
          Proposals
        </h1>
        {remaining !== undefined && (
          <span className="ml-auto text-sm whitespace-nowrap text-muted-foreground tabular-nums">
            {remaining} waiting
          </span>
        )}
      </header>
      <p className="text-sm text-muted-foreground">
        Writes an unattended agent could not confirm itself. Nothing here has
        touched a record yet.
      </p>

      <div className="relative min-h-[16rem] flex-1 select-none sm:h-[30rem] sm:flex-none">
        {isDemo && (
          <EmptyState
            title="Not available in demo mode"
            detail="Proposal review calls the live MCP edge function, which this FakeRest demo does not have."
          />
        )}

        {!isDemo && error != null && (
          <EmptyState
            title="Could not load proposals"
            detail={error instanceof Error ? error.message : "Unknown error."}
            tone="negative"
          />
        )}

        {!isDemo && error == null && isPending && (
          <div className="flex h-full items-center justify-center rounded-2xl border border-dashed">
            <Spinner />
          </div>
        )}

        {!isDemo && error == null && !isPending && isExhausted && (
          <EmptyState
            title="Nothing waiting"
            detail="Every proposal has been reviewed."
          />
        )}

        {next && (
          <div
            aria-hidden
            className="absolute inset-0 translate-y-3 scale-[0.97] rounded-2xl border bg-card opacity-70 shadow-sm"
          />
        )}

        {current && (
          <div
            className={cn(
              "absolute inset-0 touch-none",
              canAct && !rejecting
                ? "cursor-grab active:cursor-grabbing"
                : "cursor-default",
            )}
            style={outcome ? undefined : gesture.style}
            onPointerDown={
              canAct && !rejecting ? gesture.onPointerDown : undefined
            }
            onPointerMove={
              canAct && !rejecting ? gesture.onPointerMove : undefined
            }
            onPointerUp={canAct && !rejecting ? gesture.onPointerUp : undefined}
            onPointerCancel={
              canAct && !rejecting ? gesture.onPointerUp : undefined
            }
          >
            <ProposalCard proposal={current} outcome={outcome} now={now} />
            {gesture.intent && !outcome && (
              <div
                className={cn(
                  "pointer-events-none absolute inset-0 flex items-center justify-center rounded-2xl border-4",
                  gesture.intent === "right" &&
                    "border-emerald-500 text-emerald-600 dark:text-emerald-400",
                  gesture.intent === "left" &&
                    "border-rose-500 text-rose-600 dark:text-rose-400",
                  (gesture.intent === "up" || gesture.intent === "down") &&
                    "border-muted-foreground text-muted-foreground",
                )}
                style={{
                  opacity:
                    0.25 +
                    Math.min(1, Math.hypot(gesture.dx, gesture.dy) / 96) * 0.75,
                }}
              >
                <span className="rounded-lg bg-background/80 px-4 py-2 text-2xl font-bold tracking-tight uppercase">
                  {gesture.intent === "right"
                    ? "Approve"
                    : gesture.intent === "left"
                      ? "Reject"
                      : "Skip"}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {current && outcome && (
        <Button onClick={continueAfterOutcome} className="w-full">
          Continue
        </Button>
      )}

      {current && !outcome && rejecting && (
        <div className="flex flex-col gap-2 rounded-xl border p-3">
          <label
            htmlFor="proposal-reject-reason"
            className="text-xs font-semibold text-muted-foreground uppercase"
          >
            Why reject this?
          </label>
          <Textarea
            id="proposal-reject-reason"
            autoFocus
            rows={2}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="A sentence the proposer can learn from…"
          />
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={() => {
                setRejecting(false);
                setRejectionReason("");
              }}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="flex-1"
              disabled={!rejectionReason.trim() || isWorking}
              onClick={submitRejection}
            >
              {isWorking ? <Spinner className="h-4 w-4" /> : "Confirm reject"}
            </Button>
          </div>
        </div>
      )}

      {current && !outcome && !rejecting && (
        <div className="grid grid-cols-3 gap-1.5 sm:gap-2">
          <button
            type="button"
            disabled={!canAct}
            onClick={() => setRejecting(true)}
            className="flex flex-col items-center gap-1 rounded-xl border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-rose-700 transition-colors disabled:opacity-40 dark:text-rose-300"
          >
            <X className="h-4 w-4" aria-hidden />
            <span className="text-xs font-semibold">Reject</span>
          </button>
          <button
            type="button"
            disabled={!canAct}
            onClick={skip}
            className="flex flex-col items-center gap-1 rounded-xl border px-3 py-2 text-muted-foreground transition-colors disabled:opacity-40"
          >
            <SkipForward className="h-4 w-4" aria-hidden />
            <span className="text-xs font-semibold">Skip</span>
          </button>
          <button
            type="button"
            disabled={!canAct}
            onClick={approve}
            className="flex flex-col items-center gap-1 rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-emerald-700 transition-colors disabled:opacity-40 dark:text-emerald-300"
          >
            {isWorking ? (
              <Spinner className="h-4 w-4" />
            ) : (
              <Check className="h-4 w-4" aria-hidden />
            )}
            <span className="text-xs font-semibold">Approve</span>
          </button>
        </div>
      )}

      <p className="hidden text-center text-xs text-muted-foreground sm:block">
        Swipe the card, tap an answer, or use{" "}
        <kbd className="rounded border px-1">←</kbd> reject{" "}
        <kbd className="rounded border px-1">→</kbd> approve{" "}
        <kbd className="rounded border px-1">↓</kbd>/
        <kbd className="rounded border px-1">S</kbd> skip
      </p>
    </div>
  );
};

const EmptyState = ({
  title,
  detail,
  tone = "default",
}: {
  title: string;
  detail: string;
  tone?: "default" | "negative";
}) => (
  <div className="flex h-full flex-col items-center justify-center gap-3 rounded-2xl border border-dashed p-6 text-center">
    <Alert
      variant={tone === "negative" ? "destructive" : "default"}
      className="text-left"
    >
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription>{detail}</AlertDescription>
    </Alert>
    <Button variant="outline" size="sm" asChild>
      <Link to="/swipe">Back to triage</Link>
    </Button>
  </div>
);
